import React, { useMemo } from 'react';
import {
  Monitor,
  Smartphone,
  Tablet,
  Loader2,
  LogOut,
  MapPin,
  Clock,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'components/ui/use-toast';
import { cn } from 'lib/utils';
import { useLanguageStore } from 'store/language.store';
import {
  useSessions,
  useRevokeSession,
  useRevokeOtherSessions,
} from 'modules/auth/hooks';
import type { IApi } from 'modules/auth/types';

const DEVICE_ICON = {
  mobile: Smartphone,
  tablet: Tablet,
  desktop: Monitor,
} as const;

const UNKNOWN = 'Unknown';

/** Build "Chrome · macOS", dropping any part the parser couldn't identify. */
function deviceLabel(session: IApi.Sessions.Item, fallback: string): string {
  const parts = [session.browser, session.os].filter((p) => p && p !== UNKNOWN);
  return parts.length ? parts.join(' · ') : fallback;
}

function relativeTime(rtf: Intl.RelativeTimeFormat, iso: string | null): string {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diffMs)) return '';
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return rtf.format(-sec, 'second');
  const min = Math.round(sec / 60);
  if (min < 60) return rtf.format(-min, 'minute');
  const hr = Math.round(min / 60);
  if (hr < 24) return rtf.format(-hr, 'hour');
  const day = Math.round(hr / 24);
  if (day < 30) return rtf.format(-day, 'day');
  const mo = Math.round(day / 30);
  if (mo < 12) return rtf.format(-mo, 'month');
  return rtf.format(-Math.round(mo / 12), 'year');
}

interface SessionRowProps {
  session: IApi.Sessions.Item;
  dateFmt: Intl.DateTimeFormat;
  rtf: Intl.RelativeTimeFormat;
  onRevoke: (id: string) => void;
  revoking: boolean;
}

function SessionRow({ session, dateFmt, rtf, onRevoke, revoking }: SessionRowProps) {
  const { t } = useTranslation();
  const Icon = DEVICE_ICON[session.device] ?? Monitor;
  const label = deviceLabel(session, t('settings.unknown_device', 'Noma\'lum qurilma'));

  return (
    <div className="flex items-center justify-between gap-3 p-4 transition-colors hover:bg-muted/10">
      <div className="flex items-center gap-3.5 min-w-0">
        <div
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors',
            session.current
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'bg-muted text-muted-foreground',
          )}
        >
          <Icon size={18} />
        </div>

        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-foreground truncate">{label}</p>
            {session.current && (
              <span className="inline-flex items-center rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-foreground shadow-sm">
                {t('settings.this_device', 'BU QURILMA')}
              </span>
            )}
          </div>

          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
            {session.ipAddress && (
              <span className="inline-flex items-center gap-1">
                <MapPin size={11} className="shrink-0 text-muted-foreground/80" />
                {session.ipAddress}
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              <Clock size={11} className="shrink-0 text-muted-foreground/80" />
              {session.current
                ? t('settings.active_now', 'Hozir faol')
                : `${relativeTime(rtf, session.lastUsedAt)}`}
            </span>
            {session.createdAt && (
              <span className="text-muted-foreground/75">
                {t('settings.signed_in', 'Kirilgan')} · {dateFmt.format(new Date(session.createdAt))}
              </span>
            )}
          </div>
        </div>
      </div>

      {!session.current && (
        <button
          type="button"
          className="shrink-0 text-sm font-medium text-destructive transition-opacity hover:opacity-80 hover:underline disabled:opacity-50"
          onClick={() => onRevoke(session.id)}
          disabled={revoking}
        >
          {revoking ? <Loader2 size={13} className="animate-spin inline mr-1" /> : null}
          {t('settings.revoke', 'Chiqish')}
        </button>
      )}
    </div>
  );
}

export const SessionsCard: React.FC = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { language } = useLanguageStore();

  const { data: sessions, isLoading } = useSessions();
  const revokeOne = useRevokeSession();
  const revokeOthers = useRevokeOtherSessions();

  const { dateFmt, rtf } = useMemo(() => {
    const locale = language === 'uz' ? 'uz-UZ' : language;
    return {
      dateFmt: new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }),
      rtf: new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }),
    };
  }, [language]);

  // Current device first, then whatever order the API returned (activity desc).
  const ordered = useMemo(
    () => [...(sessions ?? [])].sort((a, b) => Number(b.current) - Number(a.current)),
    [sessions],
  );
  const othersCount = ordered.filter((s) => !s.current).length;

  const handleRevoke = (id: string) => {
    revokeOne.mutate(id, {
      onError: () => toast.error(t('something_went_wrong', 'Something went wrong')),
    });
  };

  const handleRevokeOthers = () => {
    revokeOthers.mutate(undefined, {
      onError: () => toast.error(t('something_went_wrong', 'Something went wrong')),
    });
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
      <div className="p-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted/80 text-foreground">
            <Monitor size={18} className="text-muted-foreground" />
          </div>
          <div>
            <h3 className="text-base sm:text-lg font-semibold tracking-tight text-foreground">
              {t('settings.sessions', 'Faol seanslar')}
            </h3>
            <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
              {t('settings.sessions_note', 'Hisobingizga kirgan qurilma va brauzerlar. Tanimaganlaringizni chiqaring.')}
            </p>
          </div>
        </div>

        <div className="mt-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 size={20} className="animate-spin" />
            </div>
          ) : ordered.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {t('settings.sessions_empty', 'Faol seanslar yo\'q.')}
            </p>
          ) : (
            <div className="h-72 overflow-y-auto overscroll-contain rounded-xl border border-border divide-y divide-border bg-background/50">
              {ordered.map((s) => (
                <SessionRow
                  key={s.id}
                  session={s}
                  dateFmt={dateFmt}
                  rtf={rtf}
                  onRevoke={handleRevoke}
                  revoking={revokeOne.isPending}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {othersCount > 0 && (
        <div className="flex justify-end border-t border-border/50 bg-muted/20 px-6 py-3.5">
          <button
            type="button"
            onClick={handleRevokeOthers}
            disabled={revokeOthers.isPending}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-5 py-2 text-xs sm:text-sm font-medium text-foreground transition-all hover:bg-accent active:scale-95 disabled:pointer-events-none disabled:opacity-50 shadow-sm"
          >
            {revokeOthers.isPending ? (
              <Loader2 size={14} className="animate-spin text-muted-foreground" />
            ) : (
              <LogOut size={14} className="text-muted-foreground" />
            )}
            <span>
              {t('settings.sign_out_others', 'Boshqa qurilmalardan chiqish')} ({othersCount})
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

export default SessionsCard;
